import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { AccountsTablePersistedPayload } from './accountsPreferences';
import { persistAccountsTablePrefs } from './accountsPreferences';
import { logger } from '../../logging';
import type { FrozenAccountsTablePrefs } from './useFrozenAccountsTablePrefs';
import { hydrateAccountsTablePrefs } from './accountsTablePrefsHydration';
import {
  getAccountsTableColumnFilters,
  getAccountsTableGlobalFilter,
  setAccountsTableColumnFilters,
  setAccountsTableGlobalFilter,
} from '../../components/accountsTableFilterSession';

const PERSIST_DEBOUNCE_MS = 400;

/**
 * Pushes RTDB "frozen" prefs into react-table before paint, then debounces writes back.
 * Dotdigital passes filtersOpen/setFiltersOpen; Slicktext sets withGlobalFilter.
 */
export type UseAccountsTablePrefsSyncParams = {
  uid: string;
  tableName: string;
  prefsReady: boolean;
  frozenPrefs: FrozenAccountsTablePrefs | null;
  tableData: any;
  activeTab: number;
  setActiveTab: (tab: number) => void;
  /** Dotdigital: panel open state + setter */
  filtersOpen?: boolean;
  setFiltersOpen?: (v: boolean) => void;
  /** Slicktext: react-table global filter */
  withGlobalFilter?: boolean;
};

export function useAccountsTablePrefsSync({
  uid,
  tableName,
  prefsReady,
  frozenPrefs,
  tableData,
  activeTab,
  setActiveTab,
  filtersOpen,
  setFiltersOpen,
  withGlobalFilter,
}: UseAccountsTablePrefsSyncParams): void {
  const [persistAllowed, setPersistAllowed] = useState(false);
  const frozenKeyRef = useRef<string | null>(null);
  const skipMemoryWriteRef = useRef(true);

  const syncsFiltersOpen = typeof setFiltersOpen === 'function';

  useLayoutEffect(() => {
    if (!frozenPrefs) {
      return;
    }
    // Re-apply when savedPrefs updates (e.g. after refresh) but skip duplicate work.
    const key = JSON.stringify({
      activeTab: frozenPrefs.activeTab,
      filtersOpen: frozenPrefs.filtersOpen,
      sortBy: frozenPrefs.table.sortBy,
      pageIndex: frozenPrefs.table.pageIndex,
      pageSize: frozenPrefs.table.pageSize,
    });
    if (key === frozenKeyRef.current) {
      return;
    }
    frozenKeyRef.current = key;
    setActiveTab(frozenPrefs.activeTab);
    if (setFiltersOpen) {
      setFiltersOpen(frozenPrefs.filtersOpen);
    }
    skipMemoryWriteRef.current = true;
    hydrateAccountsTablePrefs(tableData, frozenPrefs.table, !!withGlobalFilter, {
      filters: getAccountsTableColumnFilters(uid, tableName),
      globalFilter: getAccountsTableGlobalFilter(uid, tableName),
    });
    setPersistAllowed(true);
  }, [frozenPrefs, tableData, setActiveTab, setFiltersOpen, syncsFiltersOpen, withGlobalFilter, uid, tableName]);

  // Do not persist until layout has applied frozenPrefs once (avoids clobbering server).
  useEffect(() => {
    if (!uid || !prefsReady || !persistAllowed) {
      return;
    }
    const handle = window.setTimeout(() => {
      const payload: AccountsTablePersistedPayload = {
        activeTab,
        sortBy: tableData.state.sortBy ?? [],
        pageIndex: tableData.state.pageIndex ?? 0,
        pageSize: tableData.state.pageSize ?? 20,
      };
      if (syncsFiltersOpen && typeof filtersOpen === 'boolean') {
        payload.filtersOpen = filtersOpen;
      }
      persistAccountsTablePrefs(uid, tableName, payload).catch((err) =>
        logger.error(`persistAccountsTablePrefs: ${err}`)
      );
    }, PERSIST_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [
    uid,
    tableName,
    prefsReady,
    persistAllowed,
    activeTab,
    syncsFiltersOpen,
    filtersOpen,
    withGlobalFilter,
    tableData.state.sortBy,
    tableData.state.pageIndex,
    tableData.state.pageSize,
  ]);

  useEffect(() => {
    if (!uid || !persistAllowed) {
      return;
    }
    if (skipMemoryWriteRef.current) {
      skipMemoryWriteRef.current = false;
      return;
    }
    setAccountsTableColumnFilters(
      uid,
      tableName,
      tableData.state.filters ?? []
    );
    if (withGlobalFilter) {
      setAccountsTableGlobalFilter(
        uid,
        tableName,
        tableData.state.globalFilter ?? ''
      );
    }
  }, [
    uid,
    tableName,
    persistAllowed,
    withGlobalFilter,
    tableData.state.filters,
    tableData.state.globalFilter,
  ]);
}
